import {
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IEmptyProps from "./type";
import type {
    EmptyContextType
} from "./type";
import stylesheet, {
    getEmptyMeasures,
    getEmptyColors,
    useStyles
} from "./stylesheet";
import EmptyContent from "./components/content";
import EmptyDescription from "./components/description";
import EmptyHeader from "./components/header";
import EmptyMedia from "./components/media";
import EmptyTitle from "./components/title";
import {
    EmptyContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    SURFACE_CONTAINER_COLOR
} from "../../helpers/theme/elevation";
import Skeleton from "../skeleton";

const Empty: FC<IEmptyProps> = ({
    "aria-busy": ariaBusy,
    isLoading = false,
    variant = "plain",
    type = "neutral",
    size = "medium",
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        surfaceColor: parentSurfaceColor,
        controlHeights,
        shapeConfigs,
        avatarSizes,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const currentMeasures = useMemo(() => getEmptyMeasures({
        controlHeights,
        avatarSizes,
        iconSizes,
        variant,
        spaces,
        size
    }), [
        controlHeights,
        avatarSizes,
        iconSizes,
        variant,
        spaces,
        size
    ]);

    const currentColors = useMemo(() => getEmptyColors({
        colors,
        type
    }), [
        colors,
        type
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        currentMeasures,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        variant,
        colors
    });

    const context = useMemo<EmptyContextType>(() => ({
        measures: currentMeasures,
        colors: currentColors,
        customTheme,
        isLoading,
        type
    }), [
        currentMeasures,
        currentColors,
        customTheme,
        isLoading,
        type
    ]);

    const currentStyle = [
        style,
        stylesheet.container,
        containerDynamicStyle
    ];

    const renderContainer = () => {
        if(ariaBusy !== undefined) {
            return <View
                {...props}
                aria-busy={ariaBusy}
                style={currentStyle}
            >
                {children}
            </View>;
        }

        return <Skeleton.Group
            {...props}
            placeholder={isLoading ? children : null}
            customTheme={customTheme}
            isLoading={isLoading}
            style={currentStyle}
        >
            {isLoading ? null : children}
        </Skeleton.Group>;
    };

    return <EmptyContext.Provider
        value={context}
    >
        <NCoreUIKitTheme.SurfaceProvider
            surfaceColor={variant === "card" ? SURFACE_CONTAINER_COLOR : parentSurfaceColor}
        >
            {renderContainer()}
        </NCoreUIKitTheme.SurfaceProvider>
    </EmptyContext.Provider>;
};
export default Object.assign(Empty, {
    Description: EmptyDescription,
    emptyPart: "empty" as const,
    Content: EmptyContent,
    Header: EmptyHeader,
    Media: EmptyMedia,
    Title: EmptyTitle
});
